import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from '@/lib/api/client'
import type { Discount, Resource } from '@/lib/api/types'

export interface DiscountList {
  data: Discount[]
  meta: {
    targets: Record<string, string>
    can_edit: boolean
    test_mode: boolean
    synced_at?: string | null
    /** Taken off payments by discounts (USD cents), all codes together: payments don't say which code. */
    given?: { this_month: number; last_month: number; all_time: number; payments_this_month: number }
  }
}

export interface NewDiscount {
  name: string
  code: string
  amount_type: 'percent' | 'fixed'
  amount: number
  duration: 'once' | 'repeating' | 'forever'
  duration_in_months?: number | null
  applies_to?: string[]
  max_redemptions?: number | null
  expires_at?: string | null
  workspace_id?: string | null
}

export function useDiscounts() {
  return useQuery({ queryKey: ['discounts'], queryFn: () => http.get<DiscountList>('/discounts') })
}

export function useCreateDiscount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: NewDiscount) => (await http.post<Resource<Discount>>('/discounts', input)).data,
    onSuccess: (d) => {
      void queryClient.invalidateQueries({ queryKey: ['discounts'] })
      if (d.customer) void queryClient.invalidateQueries({ queryKey: ['customer', d.customer.id] })
    },
  })
}

export function useRemoveDiscount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => (await http.delete<Resource<Discount>>(`/discounts/${id}`)).data,
    onSuccess: (d) => {
      void queryClient.invalidateQueries({ queryKey: ['discounts'] })
      if (d.customer) void queryClient.invalidateQueries({ queryKey: ['customer', d.customer.id] })
    },
  })
}

/** Brings the list in step with Lemon Squeezy now (it also happens every hour). */
export function useSyncDiscounts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      await http.post<DiscountList & { synced: { added: number; updated: number; removed: number } }>(
        '/discounts/sync',
      ),
    onSuccess: (data) => queryClient.setQueryData(['discounts'], data),
  })
}
